import * as React from 'react';
import './RolesPermissions.scss';
import { Icon } from '@teamsnap/snap-ui';
import { Button, FieldGroup, FieldLabel, FieldMessage, Loader, Select } from '@teamsnap/teamsnap-ui';
import MultiSelectPicker from '../MultiSelectPicker/MultiSelectPicker';

export enum Role {
  OrgAdmin = 'organization_admin',
  ProgramAdmin = 'program_admin',
  ProgramCoordinator = 'program_coordinator',
  Owner = 'owner',
  Admin = 'admin',
  DivisionCoordinator = 'division_coordinator',
}

export type RefProps = { loadDivisions: (...seasonIds: string[]) => Promise<void> };

export type RolesAndPermissionsProps = {
  role: string;
  roleError: boolean;
  season: string;
  seasonError: boolean;
  programs: string[];
  programsError: boolean;
  divisions: string[];
  divisionsError: boolean;
};

export type SeasonWithDivisionsResponse = {
  id: number;
  divisions: {
    id?: number;
    name?: string;
  }[];
};

type Props = {
  isLoadingDivisions: boolean;
  programs: { value: string; label: string }[];
  seasons: { value: string; label: string }[];
  roles: RolesAndPermissionsProps[];
  setRoles: (x: RolesAndPermissionsProps[]) => void;
  queryDivisions: (params: {
    variables: { seasonId: number };
  }) => Promise<{ data: { showSeason: SeasonWithDivisionsResponse } }>;
};

type SeasonDivisions = { [seasonId: string]: string | { value: string; label: string }[] };

export const defaultRole: RolesAndPermissionsProps = {
  role: '',
  roleError: false,
  season: '',
  seasonError: false,
  programs: [],
  programsError: false,
  divisions: [],
  divisionsError: false,
};

const convertDivisions = (data: { showSeason: SeasonWithDivisionsResponse }) =>
  data.showSeason.divisions
    .map((x) => ({ value: `${x.id}`, label: x.name }))
    .slice()
    .sort((a, b) => a.label.localeCompare(b.label));

const RolesAndPermissions = React.forwardRef(
  (
    { programs, seasons, roles, setRoles, isLoadingDivisions, queryDivisions }: Props,
    ref: React.ForwardedRef<RefProps>
  ) => {
    const [seasonDivisions, setSeasonDivisions] = React.useState<SeasonDivisions>({});

    const loadDivisions = React.useCallback(
      async (...seasonIds: string[]) => {
        let state: SeasonDivisions = { ...seasonDivisions };
        for (const seasonId of seasonIds) {
          if (seasonId && !seasonDivisions[seasonId]) {
            setSeasonDivisions({ ...seasonDivisions, [seasonId]: 'loading' });
            const { data } = await queryDivisions({ variables: { seasonId: +seasonId } });

            state = { ...state, [seasonId]: convertDivisions(data) };
          }
        }
        setSeasonDivisions(state);
      },
      [seasonDivisions, setSeasonDivisions, queryDivisions]
    );

    React.useImperativeHandle(ref, () => ({ loadDivisions }), [loadDivisions]);

    const setRole =
      (index: number, key: 'role' | 'season' | 'programs' | 'divisions') => (value: string | string[]) => {
        const hasError = typeof value === 'string' ? !value : !value.length;
        setRoles(roles.map((x, i) => (i !== index ? x : { ...x, [key]: value, [`${key}Error`]: hasError })));
      };

    return (
      <>
        { roles.map((data, index) => (
          <div key={ index } className={ `u-flex u-flexCol ${index ? 'roles-divider' : ''}` }>
            <FieldGroup mods="u-spaceTopMd" status={ data.roleError ? 'error' : undefined }>
              <FieldLabel name={ `roles[${index}].role` }>
                Role<span className="sui-text-red-40">*</span>
              </FieldLabel>
              <Select
                name={ `roles[${index}].role` }
                className="SelectBox SelectBox--large"
                inputProps={ {
                  value: data.role,
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => setRole(index, 'role')(e.target.value),
                } }
                options={ [
                  { label: '', value: '' },
                  { label: 'Organization Admin', value: Role.OrgAdmin },
                  { label: 'Program Admin', value: Role.ProgramAdmin },
                  { label: 'Program Coordinator', value: Role.ProgramCoordinator },
                  { label: 'Division Coordinator', value: Role.DivisionCoordinator },
                ] }
              />
              <FieldMessage status={ data.roleError ? 'error' : undefined }>
                { `
            ${data.roleError ? 'Role is required' : ''}
            ${data.role === Role.OrgAdmin ? 'Access to all areas of the org' : ''}
            ${data.role === Role.ProgramAdmin ? 'Access to all seasonal areas of assigned programs' : ''}
            ${
              data.role === Role.DivisionCoordinator
                ? 'Access to all areas of assigned divisions, except financial information on financials, invoicing, and registration pages.'
                : ''
            }
            ${
              data.role === Role.ProgramCoordinator
                ? 'Access to most seasonal areas of assigned program, except financial information on financials, invoicing, and registration pages.'
                : ''
            }
          ` }
              </FieldMessage>
            </FieldGroup>

            <FieldGroup
              mods={ `${
                [Role.ProgramAdmin, Role.ProgramCoordinator].includes(data.role as Role) ? 'u-spaceTopMd' : 'u-hidden'
              }` }
              status={ data.programsError ? 'error' : undefined }
            >
              <FieldLabel name={ `roles[${index}].programs` }>
                Programs<span className="sui-text-red-40">*</span>
              </FieldLabel>
              <MultiSelectPicker
                options={ programs.slice().sort((a, b) => a.label.localeCompare(b.label)) }
                selected={ data.programs }
                updateSelectedOptions={ setRole(index, 'programs') }
              />
              <FieldMessage status={ data.programsError ? 'error' : undefined }>
                { data.programsError ? 'Select at least one program' : '' }
              </FieldMessage>
            </FieldGroup>

            { data.role === Role.DivisionCoordinator && (
              <>
                <FieldGroup mods="u-spaceTopMd" status={ data.seasonError ? 'error' : undefined }>
                  <FieldLabel name={ `roles[${index}].season` }>
                    Season<span className="sui-text-red-40">*</span>
                  </FieldLabel>
                  <Select
                    disabled={ isLoadingDivisions }
                    name={ `roles[${index}].season` }
                    className="SelectBox SelectBox--large"
                    inputProps={ {
                      value: data.season,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        setRole(index, 'season')(e.target.value);
                        loadDivisions(e.target.value);
                      },
                    } }
                    options={ [
                      { label: '', value: '' },
                      ...seasons.slice().sort((a, b) => a.label.localeCompare(b.label)),
                    ] }
                  />
                  <FieldMessage status={ data.seasonError ? 'error' : undefined }>
                    { data.seasonError ? 'Season is required' : '' }
                  </FieldMessage>
                </FieldGroup>

                <FieldGroup mods="u-spaceTopMd" status={ data.divisionsError ? 'error' : undefined }>
                  <FieldLabel name={ `roles[${index}].divisions` }>
                    Divisions<span className="sui-text-red-40">*</span>
                  </FieldLabel>
                  { seasonDivisions[data.season] === 'loading' && <Loader type="spin" text="Loading..." /> }
                  { !seasonDivisions[data.season] && (
                    <MultiSelectPicker disabled options={ [] } selected={ [] } updateSelectedOptions={ () => {} } />
                  ) }
                  { Array.isArray(seasonDivisions[data.season]) && data.divisions && (
                    <MultiSelectPicker
                      options={ seasonDivisions[data.season] as [] }
                      selected={ data.divisions }
                      updateSelectedOptions={ setRole(index, 'divisions') }
                    />
                  ) }
                  <FieldMessage status={ data.divisionsError ? 'error' : undefined }>
                    { data.divisionsError ? 'Select at least one division' : '' }
                  </FieldMessage>
                </FieldGroup>
              </>
            ) }

            { roles.length > 1 && (
              <Button
                type="link"
                testId="remove-role"
                mods="sui-text-red-40 delete-button"
                onClick={ () => setRoles(roles.filter((_, i) => i !== index)) }
              >
                <Icon size="s" name="delete" />
                Remove
              </Button>
            ) }
          </div>
        )) }

        <Button
          type="link"
          testId="add-role"
          mods={ roles.length === 1 ? 'u-spaceTopMd' : '' }
          onClick={ () => setRoles([...roles, defaultRole]) }
        >
          + Add another role
        </Button>
      </>
    );
  }
);

export default RolesAndPermissions;
